import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { apiClient } from "@/lib/client";
import { ENDPOINTS } from "@/api/endpoints";

interface InventoryDistribution {
  occasion: Record<string, number>;
  colour: Record<string, number>;
  articleType: Record<string, number>;
  season: Record<string, number>;
  brand: Record<string, number>;
}
interface TransformedInventoryDistribution {
  occasion: Array<{ type: string; value: number }>;
  colour: Array<{ type: string; value: number }>;
  articleType: Array<{ type: string; value: number }>;
  season: Array<{ type: string; value: number }>;
  brand: Array<{ type: string; value: number }>;
}

interface GetInventoryDistributionParams {
  jobId: string;
  date: string;
}

const getInventoryDistribution = async ({
  jobId,
  date,
}: GetInventoryDistributionParams): Promise<InventoryDistribution> => {
  return apiClient
    .get(ENDPOINTS.GET_INSIGHTS_INVENTORY_DISTRIBUTION(jobId, date))
    .json<InventoryDistribution>();
};

const transformData = (data: Record<string, number>) =>
  Object.entries(data)
    .sort(([, a], [, b]) => b - a)
    .reduce((acc, [type, value], index) => {
      if (index < 6) {
        acc.push({
          type,
          value: Number(value.toFixed(2)),
        });
      } else if (acc[6]) {
        acc[6].value = Number((acc[6].value + Number(value)).toFixed(2));
      } else {
        acc.push({
          type: "Other",
          value: Number(value.toFixed(2)),
        });
      }
      return acc;
    }, [] as { type: string; value: number }[]);

export const useGetInventoryDistributionQuery = (
  params: GetInventoryDistributionParams,
  options?: Omit<
    UseQueryOptions<
      InventoryDistribution,
      Error,
      TransformedInventoryDistribution
    >,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["inventoryDistribution", params.jobId, params.date],
    queryFn: () => getInventoryDistribution(params),
    select: (data) => ({
      occasion: transformData(data.occasion),
      colour: transformData(data.colour),
      articleType: transformData(data.articleType),
      season: transformData(data.season),
      brand: transformData(data.brand),
    }),
    ...options,
  });
};
